import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

const faqItems = [
  {
    question: 'What is A2P 10DLC?',
    answer:
      'A2P 10DLC (Application-to-Person 10-Digit Long Code) is a system that allows businesses to send text messages through standard 10-digit phone numbers. It requires registration with The Campaign Registry (TCR) to ensure messages are legitimate and compliant with carrier regulations.',
  },
  {
    question: 'What types of messages will I receive?',
    answer:
      'You may receive appointment reminders and confirmations, service updates and announcements, marketing promotions and special offers, follow‑up communications regarding your inquiries, and tips related to our AI receptionist services.',
  },
  {
    question: 'How do I opt out of text messages?',
    answer:
      'You can opt out at any time by replying STOP to any message you receive from us. You will receive a one‑time confirmation message, and no further messages will be sent. You can also contact us at info@atlassystem.io to request removal.',
  },
  {
    question: 'Will I be charged for text messages?',
    answer:
      'Standard message and data rates from your mobile carrier may apply. Atlas System does not charge any additional fees for sending or receiving text messages through our program.',
  },
  {
    question: 'Is my phone number shared with third parties?',
    answer:
      'No. We do not sell, rent, lease, or share your mobile phone number or any information collected through our SMS program with any third parties for their own marketing purposes. Your number is only shared with our messaging platform provider who is contractually prohibited from using it for any other purpose.',
  },
  {
    question: 'How often will I receive messages?',
    answer:
      'Message frequency varies based on your interactions with us and the services you use. Typically, you can expect to receive no more than 5‑10 messages per month. You can adjust your preferences or opt out at any time.',
  },
];

export default function SmsOptInPage() {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Handle nav background change on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure page starts at top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/demo"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Demo
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <Link
              to="/start-free-trial"
              className="px-6 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Opt-In Form */}
      <section className="pt-32 pb-20 bg-gray-50" id="opt-in-form">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">
              OPT IN
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              SMS Text Messaging
              <br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">
                Opt‑In &amp; Compliance
              </span>
            </h1>
            <p className="text-sm text-gray-600">
              Fill out the form below to opt in to receive text messages from Atlas System.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <iframe
              src="https://link.atlassystem.io/widget/form/q6mYl9mgCGx5pB5VtZFN"
              style={{ width: '100%', height: '1200px', border: 'none' }}
              id="inline-q6mYl9mgCGx5pB5VtZFN"
              title="SMS Opt-In Form"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">
              FAQ
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqItems.map((item, i) => (
              <div
                key={i}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span itemProp="name" className="text-sm font-semibold text-gray-900 pr-4">
                    {item.question}
                  </span>
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-gray-400 text-xl transition-transform duration-300 ${
                        openFaq === i ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === i ? 'max-h-96' : 'max-h-0'
                  }`}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div className="px-5 pb-5">
                    <p itemProp="text" className="text-sm text-gray-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
